/**
 * 浮游对战界面（覆盖在金字塔之上）
 *
 * 核心机制（最新）：
 *  - 两人偶位于左右两侧
 *  - 中间"扁长灰色赛道" + "能量对冲彩色圆球"
 *  - 双方光波**始终贴在自己人偶位置持续发射**（不是某时刻切段）
 *  - 强者光波向右推 → 头尖持续延伸到对方人偶位置（接触）
 *  - 弱者光波被反向推回 → 头尖向自身方向收缩
 *  - **接触点 = 强者光波的头尖位置** = 能量圆球位置（实时跟随）
 *  - 胜利者光波头尖**接触到对方人偶** → 结算
 *
 * 胜负页面：
 *  - 守擂者（defender = 观众）胜 → 胜利页面（绿色 VICTORY） → "确定"按钮
 *  - 守擂者败 → 失败页面（红色 DEFEAT） → "重新挑战 / 放弃"按钮
 */

import { drawFigure } from "./figureSprite";
import { hashStr } from "./hashUtil";
import { getSfx } from "./bgm";
import {
  simulateDuel,
  getBeamColorForRank,
  type Fighter,
  type DuelResult,
} from "./duel";
import { RANK_LABELS } from "./ranking";
import type { Element } from "../shared/types";

export interface DuelOverlayOptions {
  challenger: Fighter;
  defender: Fighter;
  onClose: () => void;
  /**
   * 对战结果回调（可选）
   *  - 胜者：挑战者赢了 → winnerScpId === challenger.scpId；反之 === defender.scpId
   *  - 用户要求：胜方互换阶级 — 通常由调用方通过 updateTier/swapRanks 持久化
   */
  onDuelResult?: (payload: { winnerScpId: string; loserScpId: string }) => void;
}

type Phase = "intro" | "beam" | "result";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  size: number;
  color: string;
}

export function showDuelOverlay(opts: DuelOverlayOptions): { destroy: () => void } {
  const { challenger, defender, onClose, onDuelResult } = opts;

  const overlay = document.createElement("div");
  overlay.className = "duel-overlay";

  const box = document.createElement("div");
  box.className = "duel-box";
  overlay.appendChild(box);

  const title = document.createElement("div");
  title.className = "duel-title";
  title.innerHTML = `<span>防守对波</span><span class="duel-x">⚡</span><span>${challenger.name} 来袭</span>`;
  box.appendChild(title);

  const closeBtn = document.createElement("button");
  closeBtn.className = "duel-close";
  closeBtn.innerHTML = "×";
  closeBtn.title = "返回";
  closeBtn.type = "button";
  box.appendChild(closeBtn);

  const stage = document.createElement("div");
  stage.className = "duel-stage";
  box.appendChild(stage);

  // 扁长灰色赛道
  const lane = document.createElement("div");
  lane.className = "duel-lane";
  stage.appendChild(lane);

  // 双方人偶
  const leftFig = document.createElement("div");
  leftFig.className = "duel-fighter duel-fighter-left";
  stage.appendChild(leftFig);

  const rightFig = document.createElement("div");
  rightFig.className = "duel-fighter duel-fighter-right";
  stage.appendChild(rightFig);

  // 光波 canvas（透明，只覆盖对波区）
  const beamCanvas = document.createElement("canvas");
  beamCanvas.className = "duel-beam";
  beamCanvas.width = 540;
  beamCanvas.height = 220;
  stage.appendChild(beamCanvas);

  // 中央能量对冲圆球（DOM，跟随接触点移动）
  const orb = document.createElement("div");
  orb.className = "duel-orb";
  orb.innerHTML = `<div class="orb-core"></div><div class="orb-glow"></div>`;
  stage.appendChild(orb);

  // 胜负页面（默认隐藏）
  const resultPanel = document.createElement("div");
  resultPanel.className = "duel-result";
  resultPanel.style.display = "none";
  stage.appendChild(resultPanel);

  // 底部按钮区
  const actions = document.createElement("div");
  actions.className = "duel-actions";
  box.appendChild(actions);

  document.body.appendChild(overlay);

  drawFighter(leftFig, defender);
  drawFighter(rightFig, challenger);
  initOrbColors();

  let phase: Phase = "intro";
  let result: DuelResult | null = null;
  let particles: Particle[] = [];
  let beamRAF = 0;
  let beamStartTs = 0;
  let destroyed = false;
  let flashEl: HTMLElement | null = null;

  /** 真正销毁整个浮层（DOM 移除 + 调用 onClose） */
  function fullDestroy() {
    if (destroyed) return;
    destroyed = true;
    cancelAnimationFrame(beamRAF);
    try { flashEl?.remove(); } catch (_) {}
    flashEl = null;
    try { getSfx().stopAll(); } catch (_) {}
    try { onClose(); } catch (_) {}
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
  }

  function drawFighter(host: HTMLElement, f: Fighter) {
    host.innerHTML = "";
    const tag = document.createElement("div");
    tag.className = `duel-tag element-${f.element}`;
    tag.innerHTML = `<span class="dt-name">${f.name}</span><span class="dt-rank">${f.rankLabel}</span><span class="dt-el">${f.element}</span><span class="dt-score">分 ${f.total}</span>`;
    host.appendChild(tag);

    const canvas = document.createElement("canvas");
    canvas.width = 32;
    canvas.height = 32;
    canvas.className = "duel-figure-canvas";
    host.appendChild(canvas);
    drawFigure(canvas, f.element, f.rank as 1 | 2 | 3 | 4 | 5, hashStr(f.scpId));
  }

  function initOrbColors() {
    const c = getBeamColorForRank(challenger.element, challenger.rank);
    const d = getBeamColorForRank(defender.element, defender.rank);
    const orbCore = orb.querySelector(".orb-core") as HTMLElement;
    const orbGlow = orb.querySelector(".orb-glow") as HTMLElement;
    orbCore.style.background = `radial-gradient(circle, ${c.light} 0%, ${c.main} 50%, ${d.main} 100%)`;
    orbGlow.style.background = `radial-gradient(circle, ${c.light}33 0%, transparent 70%)`;
  }

  function setActionsForPhase() {
    actions.innerHTML = "";
    if (phase === "intro") {
      const startBtn = document.createElement("button");
      startBtn.type = "button";
      startBtn.className = "primary";
      startBtn.textContent = "开始对波";
      startBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (destroyed) return;
        phase = "beam";
        setActionsForPhase();
        orb.classList.add("active");
        startBeam();
      });
      actions.appendChild(startBtn);
    } else if (phase === "result" && result) {
      const defenderWon = result.winner.scpId === defender.scpId;
      if (defenderWon) {
        // 守擂者胜 → 唯一"确定"按钮
        const ok = document.createElement("button");
        ok.type = "button";
        ok.className = "primary";
        ok.textContent = "确定";
        ok.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          fullDestroy();
        });
        actions.appendChild(ok);
      } else {
        // 守擂者败 → 重新挑战 / 放弃
        const abandon = document.createElement("button");
        abandon.type = "button";
        abandon.className = "nav-btn";
        abandon.textContent = "放弃";
        abandon.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          fullDestroy();
        });

        const retry = document.createElement("button");
        retry.type = "button";
        retry.className = "primary";
        retry.textContent = "重新挑战";
        retry.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (destroyed) return;
          // 隐藏胜负面板，恢复对波
          resultPanel.style.display = "none";
          orb.classList.remove("winner", "loser");
          orb.classList.add("active");
          result = simulateDuel(challenger, defender);
          phase = "beam";
          setActionsForPhase();
          startBeam();
        });
        actions.appendChild(abandon);
        actions.appendChild(retry);
      }
    }
  }

  /**
   * 弹出胜负页面（覆盖在舞台中央）
   * 视角：defender = 守擂者（观众自己）
   *  - defender 赢 → VICTORY（绿）
   *  - defender 输 → DEFEAT（红）
   */
  function showResultPanel(defenderWon: boolean) {
    resultPanel.style.display = "flex";
    resultPanel.className = `duel-result ${defenderWon ? "win" : "lose"}`;

    const winner = defenderWon ? defender : challenger;
    const loser = defenderWon ? challenger : defender;
    const reason = result?.reason ?? "";
    const brief = result?.brief ?? [];
    const winBeam = getBeamColorForRank(winner.element, winner.rank);
    const loseBeam = getBeamColorForRank(loser.element, loser.rank);

    resultPanel.innerHTML = `
      <div class="result-banner ${defenderWon ? "win" : "lose"}">${defenderWon ? "VICTORY" : "DEFEAT"}</div>
      <div class="result-vs">
        <div class="result-fighter winner-side">
          <div class="rf-tag">胜</div>
          <div class="result-name">${winner.name}</div>
          <div class="result-rank">${RANK_LABELS[winner.rank as 1 | 2 | 3 | 4 | 5]}</div>
          <div class="result-el" style="color:${winBeam.main}">${winner.element}</div>
          <div class="result-score">分 ${winner.total}</div>
        </div>
        <div class="result-arrow">${defenderWon ? "◀ 胜" : "◀ 胜"}</div>
        <div class="result-fighter loser-side">
          <div class="rf-tag">败</div>
          <div class="result-name">${loser.name}</div>
          <div class="result-rank">${RANK_LABELS[loser.rank as 1 | 2 | 3 | 4 | 5]}</div>
          <div class="result-el" style="color:${loseBeam.main}">${loser.element}</div>
          <div class="result-score">分 ${loser.total}</div>
        </div>
      </div>
      <div class="result-reason">${reason}</div>
      ${result?.crit ? '<div class="result-crit">✦ 暴击翻盘 ✦</div>' : ""}
      <div class="result-brief">
        <div class="brief-title">◆ 简报</div>
        ${brief.map((line) => `<div class="brief-line">${line}</div>`).join("")}
      </div>
    `;
  }

  function startBeam() {
    result = simulateDuel(challenger, defender);
    particles = [];
    beamStartTs = performance.now();

    // 启动对波持续音（光波在中线对抗）
    const sfx = getSfx();
    sfx.playBeamLoop(7);

    const ctx = beamCanvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    const w = beamCanvas.width;
    const h = beamCanvas.height;

    const defenderWon = result.winner.scpId === defender.scpId;

    const winC = defenderWon ? defender : challenger;
    const loseC = defenderWon ? challenger : defender;
    const winBeam = getBeamColorForRank(winC.element, winC.rank);
    const loseBeam = getBeamColorForRank(loseC.element, loseC.rank);

    const DURATION = 7000;
    const centerX = w / 2;

    // 双方光波头尖都在 canvas 中线（接触点 = 中线）
    const winTipX = centerX;
    const loseTipX = centerX;
    const contactX = centerX;  // 接触点固定在 canvas 中线

    // 缓存 canvas 在 stage 内的实际边界（用于精确映射 orb 位置）
    const stageRect = stage.getBoundingClientRect();
    const canvasRect = beamCanvas.getBoundingClientRect();
    // canvas 相对 stage 左边缘的像素偏移
    const canvasOffsetX = canvasRect.left - stageRect.left;
    const canvasW = canvasRect.width;       // 实际显示宽度（CSS 拉伸后）
    const canvasH = canvasRect.height;      // 实际显示高度
    const ORB_HALF = 32;
    // orb 中心精确对齐到中线（像素 left = canvasOffsetX + canvasW/2 - ORB_HALF）
    const orbBaseY = (canvasRect.top - stageRect.top) + canvasH / 2;
    const orbCenterPx = canvasOffsetX + canvasW / 2;
    orb.style.left = `${orbCenterPx - ORB_HALF}px`;
    orb.style.top = `${orbBaseY - ORB_HALF}px`;

    // 结算结束时间点
    const resultAt = DURATION + 300;  // 10.3s
    const flashAt = resultAt;          // 同步闪光
    const flashDur = 500;              // 0.5s 闪亮

    function loop(ts: number) {
      if (destroyed) return;
      const elapsed = ts - beamStartTs;
      const t01 = Math.min(1, elapsed / DURATION);

      ctx.clearRect(0, 0, w, h);

      // 双方头尖都在中线
      const winSide: "right" | "left" = defenderWon ? "left" : "right";
      const loseSide: "right" | "left" = defenderWon ? "right" : "left";

      drawBeam(ctx, w, winTipX, h / 2, winSide, winBeam.main, winBeam.light);
      drawBeam(ctx, w, loseTipX, h / 2, loseSide, loseBeam.main, loseBeam.light);

      // 火焰粒子（从中线头尖持续喷射）
      spawnFlameParticles(w, winTipX, h / 2, winSide, winBeam.main, winBeam.light, true);
      spawnFlameParticles(w, loseTipX, h / 2, loseSide, loseBeam.main, loseBeam.light, false);

      // 更新 + 画粒子
      const next: Particle[] = [];
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.022;
        if (p.life > 0) next.push(p);
      }
      particles = next;

      for (const p of particles) {
        ctx.fillStyle = p.color;
        ctx.globalAlpha = Math.max(0, p.life) * 0.85;
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
      }
      ctx.globalAlpha = 1;

      // 暴击中线爆点
      if (result?.crit && t01 > 0.85) {
        for (let i = 0; i < 6; i++) {
          const ang = (i / 6) * Math.PI * 2 + (ts / 100);
          const r = 6 + Math.random() * 12;
          ctx.fillStyle = winBeam.light;
          ctx.globalAlpha = 0.9;
          ctx.fillRect(contactX + Math.cos(ang) * r, h / 2 + Math.sin(ang) * r, 2, 2);
        }
        ctx.globalAlpha = 1;
      }

      // 能量圆球：固定在中线 + 脉动 + 缩放
      // 脉动：1 ~ 1.08 之间随时间变化
      const pulse = 1 + 0.08 * Math.sin(t01 * Math.PI * 2 * 4);
      const orbScale = pulse;
      orb.style.left = `${orbCenterPx - ORB_HALF}px`;
      orb.style.top = `${orbBaseY - ORB_HALF}px`;
      orb.style.transform = `scale(${orbScale})`;

      // === 结算流程（10.3s 后） ===
      // 阶段 1：t01 < 1 → 持续对波
      // 阶段 2：10.3s ~ 10.8s → 能量球爆开 + 画面闪亮
      // 阶段 3：10.8s ~ → 渐暗 + 显示结算页
      if (elapsed >= resultAt) {
        const flashT = Math.min(1, (elapsed - resultAt) / flashDur);
        // 闪光元素
        if (!flashEl) {
          flashEl = document.createElement("div");
          flashEl.className = "duel-flash";
          stage.appendChild(flashEl);
          // 爆开音效（仅触发一次）
          getSfx().playExplode();
        }
        if (flashT < 0.5) {
          // 0 ~ 0.25s 渐亮
          flashEl.style.opacity = String(flashT * 2);
        } else {
          // 0.25 ~ 0.5s 渐暗
          flashEl.style.opacity = String((1 - flashT) * 2);
        }
        // 能量球爆开（放大 + 淡出）
        const burstScale = 1 + flashT * 4;
        orb.style.transform = `scale(${burstScale})`;
        orb.style.opacity = String(Math.max(0, 1 - flashT * 1.4));
        if (flashT >= 1) {
          // 闪亮结束 → 渐暗 + 显示结算页
          flashEl.style.opacity = "0";
          setTimeout(() => {
            flashEl?.remove();
            flashEl = null;
          }, 100);
          phase = "result";
          orb.classList.remove("active");
          orb.classList.add(defenderWon ? "winner" : "loser");
          showResultPanel(defenderWon);
          setActionsForPhase();
          // === 触发对战结果回调（胜者 → 互换阶层）===
          if (onDuelResult) {
            try {
              if (defenderWon) {
                onDuelResult({
                  winnerScpId: defender.scpId,
                  loserScpId: challenger.scpId,
                });
              } else {
                onDuelResult({
                  winnerScpId: challenger.scpId,
                  loserScpId: defender.scpId,
                });
              }
            } catch { /* ignore */ }
          }
          return;
        }
      } else {
        // 闪光前隐藏元素
        if (flashEl) {
          flashEl.remove();
          flashEl = null;
        }
        orb.style.opacity = "1";
      }

      beamRAF = requestAnimationFrame(loop);
    }

    function spawnFlameParticles(
      cw: number,
      tipX: number,
      y: number,
      side: "left" | "right",
      color: string,
      light: string,
      isWinner: boolean
    ) {
      const count = 4 + Math.floor(Math.random() * 4);
      // 在光波长度上随机生成（从人偶位置到头尖）
      const startX = side === "right" ? cw : 0;
      const endX = tipX;
      for (let i = 0; i < count; i++) {
        const t = Math.random();
        const x = side === "right"
          ? startX - Math.random() * Math.abs(endX - startX)
          : startX + Math.random() * Math.abs(endX - startX);
        const offset = (Math.random() - 0.5) * 22;
        const size = 1 + Math.floor(Math.random() * 2);
        particles.push({
          x,
          y: y + offset,
          vx: (Math.random() - 0.5) * 0.7,
          vy: -(1.4 + Math.random() * 2.0),
          life: 0.8 + Math.random() * 0.2,
          size,
          color: Math.random() < 0.3 ? light : color,
        });
      }
      if (!isWinner) {
        // 输家粒子从光波头尖散开
        for (let i = 0; i < 2; i++) {
          particles.push({
            x: tipX,
            y: y + (Math.random() - 0.5) * 26,
            vx: side === "right" ? -(2 + Math.random() * 2) : (2 + Math.random() * 2),
            vy: (Math.random() - 0.5) * 1.5,
            life: 0.7,
            size: 1 + Math.floor(Math.random() * 2),
            color: Math.random() < 0.5 ? light : color,
          });
        }
      }
    }

    beamRAF = requestAnimationFrame(loop);
  }

  /**
   * 画一条持续光波（起点 = 人偶侧；终点 = 头尖位置 tipX）
   */
  function drawBeam(
    ctx: CanvasRenderingContext2D,
    w: number,
    tipX: number,
    y: number,
    side: "left" | "right",
    color: string,
    light: string
  ) {
    if (side === "right") {
      // 右侧人偶向左发射：光波从 x=w 起到 tipX
      const x0 = Math.min(tipX, w - 4);
      const len = w - x0;
      if (len <= 4) return;
      const beamH = 14;
      const halfH = beamH / 2;

      ctx.fillStyle = color;
      ctx.globalAlpha = 0.85;
      ctx.fillRect(x0, y - halfH, len, beamH);
      // 高光核心
      ctx.fillStyle = light;
      ctx.globalAlpha = 1;
      ctx.fillRect(x0 + 6, y - 2, len - 14, 4);
      // 头尖（朝向左 = 接近对方）
      ctx.fillStyle = light;
      ctx.globalAlpha = 0.9;
      ctx.fillRect(x0 - 1, y - 1, 3, 2);
      ctx.fillRect(x0 - 4, y, 2, 2);
      // 尾端（连接人偶位置 = 永远在 w 边缘）
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = color;
      ctx.fillRect(w - 4, y - halfH + 2, 4, beamH - 4);
    } else {
      // 左侧人偶向右发射：光波从 x=0 起到 tipX
      const len = tipX;
      if (len <= 4) return;
      const beamH = 14;
      const halfH = beamH / 2;

      ctx.fillStyle = color;
      ctx.globalAlpha = 0.85;
      ctx.fillRect(0, y - halfH, len, beamH);
      ctx.fillStyle = light;
      ctx.globalAlpha = 1;
      ctx.fillRect(8, y - 2, len - 14, 4);
      // 头尖（朝向右 = 接近对方）
      ctx.fillStyle = light;
      ctx.globalAlpha = 0.9;
      ctx.fillRect(len - 2, y - 1, 3, 2);
      ctx.fillRect(len + 1, y, 2, 2);
      // 尾端
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = color;
      ctx.fillRect(0, y - halfH + 2, 4, beamH - 4);
    }
    ctx.globalAlpha = 1;
  }

  setActionsForPhase();

  closeBtn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    fullDestroy();
  });

  // ESC 关闭
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape" && !destroyed) fullDestroy();
  };
  document.addEventListener("keydown", escHandler);

  return {
    destroy: fullDestroy,
  };
}